import React, { useCallback, useMemo, useState } from 'react';
import EntityTable from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTable';
import ProductCell from 'src/components/BeaconV2Components/ProductCell/ProductCell';
import { ProductEntity } from 'src/components/BeaconV2Components/ExperimentalLayout/types';
import MetricCell from 'src/components/BeaconV2Components/MetricCell/MetricCell';
import { INDEX_FIELDS, METRICTYPE } from 'src/utils/entityDefinitions';
import { calculatePercentChange, getAppName, safeDivide } from 'src/utils/app';
import { useAppSelector } from 'src/utils/Hooks';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import EntityTableHeader, { IconsList } from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTableHeader';
import ProductGrid, { ProductGridDatum } from 'src/components/BeaconV2Components/ProductGrid/ProductGrid';
import Pagination from 'src/components/BeaconV2Components/Pagination/Pagination';
import _get from 'lodash/get';
import { shouldHidePagination } from 'src/components/BeaconV2Components/EntityTableRefresh/utils';
import { useAtlasV2TableData } from 'src/serverProxy/useAtlasV2TableData';
import NoDataPlaceHolder from 'src/components/BeaconV2Components/common/NoDataPlaceHolder/NoDataPlaceHolder';
import { Box } from '@mui/material';
import { useMainEntityType } from 'src/utils/Hooks/reduxSelectors';
import { getWeekColumnDefinition } from 'src/components/BeaconV2Components/MetricList/headColumnDefinitions';

const PAGE_SIZE = 20;
const PRODUCT_TABLE_PAGE_SIZE = 106;

const METRICS = [
  ['purchaseRate', 'sales'],
  ['retailSales', 'sales'],
  ['unitsSold', 'sales'],
  ['retailPrice', 'sales']
];

const TableNoDataPlaceHolder = (
  <NoDataPlaceHolder
    title="Data currently unavailable"
    description="There is currently no data matching your selected criteria. Please try another query to view insights."
    flexProps={{
      paddingTop: '12.5px',
      paddingBottom: '12.5px'
    }}
  />
);

/**
 * Conversion rate grid in the traffic conversion page
 */
export default function AtlasV2ConversionProductGrid() {
  const entityType = useMainEntityType();
  const isProductEntity = entityType === 'product';
  const [page, setPage] = useState(1);
  const [productTablePage, setProductTablePage] = useState(1);
  const [viewMode, setViewMode] = useState<'tile' | 'table'>(isProductEntity ? 'table' : 'tile');
  const { startWeek: comparisonStartWeek, endWeek: comparisonEndWeek } = useAppSelector(
    (state) => state.comparisonTimePeriod
  );

  const getKey = useCallback(
    (indexName: string, fieldName: string, comparison?: boolean) => {
      const field = INDEX_FIELDS.getField(getAppName(), indexName, fieldName);
      const fieldPrefix = `${fieldName}_${field.aggregationFunctionType || field.aggregationFunction}_value`;

      return comparison ? `${fieldPrefix}_${comparisonStartWeek}_${comparisonEndWeek}` : fieldPrefix;
    },
    [comparisonEndWeek, comparisonStartWeek]
  );

  const { data: salesData, isLoading: salesLoading } = useAtlasV2TableData({
    indexName: 'sales',
    fields: [
      {
        name: 'retailSales'
      },
      {
        name: 'unitsSold'
      },
      {
        name: 'retailPrice'
      }
    ],
    groupByFieldName: isProductEntity ? 'weekId' : 'stacklineSku',
    itemsPerPage: isProductEntity ? PRODUCT_TABLE_PAGE_SIZE : PAGE_SIZE,
    pageNumber: page,
    requestId: 'traffic-conversion-rate-grid'
  });

  /**
   * Returned SKUs so that we can query traffic data for each SKU
   */
  const skus = isProductEntity
    ? []
    : salesData.map((row: { metadata: { product: ProductEntity } }) => row.metadata.product.stacklineSku);

  const { data: trafficData, isLoading: trafficLoading } = useAtlasV2TableData({
    indexName: 'traffic',
    fields: [
      {
        name: 'totalClicks'
      }
    ],
    groupByFieldName: isProductEntity ? 'weekId' : 'stacklineSku',
    itemsPerPage: isProductEntity ? PRODUCT_TABLE_PAGE_SIZE : PAGE_SIZE,
    pageNumber: 1,
    requestId: 'traffic-conversion-rate-grid-traffic',
    useGenericAdvancedSearchArgs: {
      shouldPerformFetch: !salesLoading
    },
    buildRequest: isProductEntity
      ? undefined
      : (builder) => {
          return builder.addConditionTermFilter('stacklineSku', 'should', skus);
        }
  });

  /**
   * Combines sales and traffic data and computes conversion rate
   */
  const conversionRows = useMemo(() => {
    if (isProductEntity) {
      const trafficFieldKey = getKey('traffic', 'totalClicks');
      const trafficByWeekId = trafficData.reduce((acc, data) => {
        const metric = _get(data, trafficFieldKey, 0);
        return {
          ...acc,
          [data.metadata.weekId]: {
            ...data,
            value: metric
          }
        };
      }, {});
      const rows = salesData.map((row: { metadata: { weekId: string; name: string } }) => {
        const trafficRow = trafficByWeekId[row.metadata.weekId];
        const totalClicks = _get(trafficRow, getKey('traffic', 'totalClicks'), 0);

        const weekData = {
          weekId: row.metadata.weekId,
          ...['retailSales', 'unitsSold', 'retailPrice'].reduce((acc, fieldName) => {
            const key = getKey('sales', fieldName);
            const metric = _get(row, key, 0);

            return {
              ...acc,
              [fieldName]: {
                value: metric
              }
            };
          }, {}),
          totalClicks: {
            value: totalClicks
          }
        };

        const unitsSold = _get(weekData, ['unitsSold', 'value'], 0);
        const purchaseRate = safeDivide(unitsSold, totalClicks);

        return {
          ...weekData,
          purchaseRate: {
            value: purchaseRate
          }
        };
      });
      return rows.sort((a, b) => parseInt(b.weekId, 10) - parseInt(a.weekId, 10));
    }

    const stacklineSkuToTrafficData = trafficData.reduce((acc, row: { metadata: { product: ProductEntity } }) => {
      acc[row.metadata.product.stacklineSku] = row;
      return acc;
    }, {});

    const rows = salesData.map((row: { metadata: { product: ProductEntity } }) => {
      const trafficRow = stacklineSkuToTrafficData[row.metadata.product.stacklineSku];
      const totalClicks = _get(trafficRow, getKey('traffic', 'totalClicks'), 0);
      const totalClicksComparison = _get(trafficRow, getKey('traffic', 'totalClicks', true), 0);

      const productGridDatum: ProductGridDatum = {
        product: row.metadata.product,
        ...['retailSales', 'unitsSold', 'retailPrice'].reduce((acc, fieldName) => {
          const key = getKey('sales', fieldName);
          const comparisonKey = getKey('sales', fieldName, true);

          const metric = _get(row, key, 0);
          const comparison = _get(row, comparisonKey, 0);

          return {
            ...acc,
            [fieldName]: {
              value: metric,
              difference: metric - comparison
            }
          };
        }, {}),
        totalClicks: {
          value: totalClicks,
          difference: totalClicks - totalClicksComparison
        }
      };

      const unitsSold = _get(productGridDatum, ['unitsSold', 'value'], 0);
      const unitsSoldComparison = unitsSold - _get(productGridDatum, ['unitsSold', 'difference'], 0);

      const purchaseRate = safeDivide(unitsSold, totalClicks);
      const purchaseRateComparison = safeDivide(unitsSoldComparison, totalClicksComparison);

      return {
        ...productGridDatum,
        purchaseRate: {
          value: purchaseRate,
          difference: purchaseRate - purchaseRateComparison
        }
      };
    });

    return rows;
  }, [getKey, salesData, trafficData]);

  const productTableTotalPages = Math.ceil(conversionRows.length / PAGE_SIZE);
  const productTableRows = conversionRows.slice((productTablePage - 1) * PAGE_SIZE, productTablePage * PAGE_SIZE);

  const handleNextPage = () => {
    if (isProductEntity) {
      if (productTablePage < productTableTotalPages) {
        setProductTablePage((prev) => prev + 1);
      }
      return;
    }
    if (conversionRows.length < PAGE_SIZE) {
      return;
    }
    setPage(page + 1);
  };

  const handlePreviousPage = () => {
    if (isProductEntity) {
      if (productTablePage > 1) {
        setProductTablePage((prev) => prev - 1);
      }
      return;
    }
    setPage(Math.max(page - 1, 1));
  };

  return (
    <Flex flexDirection="column">
      <EntityTableHeader
        title={`Conversion Rate by ${isProductEntity ? 'Week' : 'Product'}`}
        enableSwitchingLayouts
        defaultView={viewMode}
        handleChangeLayout={() => setViewMode(viewMode === 'table' ? 'tile' : 'table')}
        paddingTop="0px"
        iconsList={isProductEntity ? [IconsList.export, IconsList.table] : undefined}
      />
      <Flex flexDirection="column" gap={viewMode === 'table' ? 0 : 20}>
        {viewMode === 'table' ? (
          isProductEntity ? (
            <EntityTable
              skeletonRows={PAGE_SIZE}
              isLoading={salesLoading || trafficLoading}
              showPagination={false}
              shouldModifyColumnDefs={false}
              columnDefs={[
                getWeekColumnDefinition({
                  getTableColumnProps(defaults) {
                    return {
                      ...defaults,
                      style: {
                        ...defaults.style,
                        width: '235px !important'
                      }
                    };
                  }
                }),
                ...METRICS.map(([fieldName, indexName]) => {
                  const field = INDEX_FIELDS.getField(getAppName(), indexName, fieldName);

                  return {
                    headerName: field.displayName,
                    valueFormatter: (_, row: ProductGridDatum) => {
                      const metricValue = _get(row, [fieldName, 'value'], 0);
                      return <MetricCell metricType={field.metricType} value={metricValue} />;
                    }
                  };
                })
              ]}
              rowData={productTableRows}
            />
          ) : (
            <EntityTable
              skeletonRows={PAGE_SIZE}
              isLoading={salesLoading || trafficLoading}
              showPagination={false}
              shouldModifyColumnDefs={false}
              columnDefs={[
                {
                  headerName: 'Products',
                  valueFormatter: (_, row: ProductGridDatum) => (
                    <div style={{ width: '359px' }}>
                      <ProductCell
                        key={row.product.stacklineSku}
                        stacklineSku={row.product.stacklineSku}
                        title={row.product.title}
                      />
                    </div>
                  )
                },
                ...METRICS.map(([fieldName, indexName]) => {
                  const field = INDEX_FIELDS.getField(getAppName(), indexName, fieldName);
                  return {
                    headerName: field.displayName,
                    valueFormatter: (_, row: ProductGridDatum) => {
                      const metricValue = _get(row, [fieldName, 'value'], 0);
                      const comparisonValue = metricValue - _get(row, [fieldName, 'difference'], 0);
                      const percentChange = calculatePercentChange(metricValue, comparisonValue);

                      return (
                        <MetricCell
                          metricType={field.metricType}
                          value={metricValue}
                          percentChange={fieldName === 'totalClicks' ? undefined : percentChange}
                        />
                      );
                    }
                  };
                })
              ]}
              rowData={conversionRows}
              appTableProps={{
                noDataPlaceholder: TableNoDataPlaceHolder,
                sx: {
                  'tr td:first-child': {
                    width: '424px',
                    paddingRight: '40px'
                  }
                }
              }}
            />
          )
        ) : (
          <ProductGrid
            data={conversionRows}
            loading={salesLoading || trafficLoading}
            metricType={METRICTYPE.PERCENT}
            fieldName="purchaseRate"
            page={1}
            noDataPlaceholder={
              <Box sx={{ display: 'flex', justifyContent: 'center', margin: '80px 0' }}>
                <NoDataPlaceHolder
                  title="Data currently unavailable"
                  description="There is currently no data matching your selected criteria. Please try another query to view insights."
                  flexProps={{
                    padding: 0
                  }}
                />
              </Box>
            }
          />
        )}
        {shouldHidePagination({ page, pageSize: PAGE_SIZE, rowCount: (conversionRows || []).length }) ? null : (
          <Flex justifyContent="flex-end">
            <Pagination
              currentPage={isProductEntity ? productTablePage : page}
              shouldUseInfinitePagination
              onClickNext={handleNextPage}
              onClickPrevious={handlePreviousPage}
            />
          </Flex>
        )}
      </Flex>
    </Flex>
  );
}
